import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode, RefObject } from 'react';
import { clsx } from 'clsx';
import { X, ArrowRight } from 'lucide-react';

// Global stack of modal onClose callbacks — topmost modal is last.
const modalStack: Array<() => void> = [];

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  footer?: ReactNode;
  /** Force mobile mode (full-screen). If not specified, auto-detects based on viewport width. */
  forceMobile?: boolean;
  /** When false, clicking the backdrop does NOT close the modal (only X/Cancel/ESC). Default true. */
  closeOnBackdropClick?: boolean;
  /** Takes focus on open instead of the panel itself. A child's own `autoFocus` still wins. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  backdropClassName?: string;
  panelClassName?: string;
  headerClassName?: string;
  contentClassName?: string;
  footerClassName?: string;
  mobileRootClassName?: string;
}

/**
 * Hook to detect mobile viewport
 */
function useIsMobile(breakpoint = 768): boolean {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < breakpoint;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);

    const handleChange = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
    };

    // Set initial value
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync to matchMedia source
    setIsMobile(mediaQuery.matches);

    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [breakpoint]);

  return isMobile;
}

/**
 * A dialog for screen readers (`role="dialog"`, `aria-modal`, named by its title). On open,
 * focus moves in: a child's `autoFocus` wins, then `initialFocusRef`, then the panel itself,
 * so no field, menu or on-screen keyboard opens unasked. On close, it returns to what had
 * focus before the dialog opened, if that is still on the page.
 */
export function Modal(props: ModalProps) {
  // Use proper mobile detection
  const detectedMobile = useIsMobile();
  // The dialog mounts only while open, so its first render sees what had focus before.
  return props.isOpen ? (
    <OpenModal {...props} isMobile={props.forceMobile ?? detectedMobile} />
  ) : null;
}

function OpenModal({
  onClose,
  title,
  children,
  size = 'md',
  footer,
  isMobile,
  closeOnBackdropClick = true,
  initialFocusRef,
  backdropClassName,
  panelClassName,
  headerClassName,
  contentClassName,
  footerClassName,
  mobileRootClassName,
}: ModalProps & { isMobile: boolean }) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  // Read while rendering, before a child's autoFocus (applied during the commit) moves it.
  const [opener] = useState(() => document.activeElement);
  // Screen readers skip what's outside an aria-modal panel, the portalled DatePicker calendar too.
  const dialogProps = {
    role: 'dialog',
    'aria-modal': true,
    'aria-labelledby': title ? titleId : undefined,
    tabIndex: -1,
  } as const;

  // ponytail: no focus trap. Portalled children (the DatePicker calendar) live outside the
  // panel, and a trap would lock them out.
  useEffect(() => {
    const panel = panelRef.current;
    // Opened as another dialog closed (one prompt leading to the next): the opener was in
    // that dialog, whose close has just handed focus back to where it came from.
    const returnTo = opener?.isConnected ? opener : document.activeElement;
    // A child that took focus with autoFocus keeps it.
    if (panel && !panel.contains(document.activeElement)) {
      initialFocusRef?.current?.focus();
      if (!panel.contains(document.activeElement)) panel.focus();
    }
    return () => {
      // Only if focus went down with the dialog: focus the app moved on purpose stays put.
      const active = document.activeElement;
      const lost = !active || active === document.body || !active.isConnected;
      if (lost && returnTo instanceof HTMLElement && returnTo.isConnected) returnTo.focus();
    };
  }, [opener, initialFocusRef]);

  // Handle ESC key — only the topmost modal should close.
  // We maintain a global stack of close callbacks so that ESC only
  // triggers the most recently opened modal's onClose.
  useEffect(() => {
    modalStack.push(onClose);
    document.body.style.overflow = 'hidden';

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && modalStack[modalStack.length - 1] === onClose) {
        e.stopImmediatePropagation();
        onClose();
      }
    };

    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('keydown', handleEsc);
      const idx = modalStack.lastIndexOf(onClose);
      if (idx !== -1) modalStack.splice(idx, 1);
      if (modalStack.length === 0) {
        document.body.style.overflow = 'unset';
      }
    };
  }, [onClose]);

  // Size classes - exact match to EM Modal
  const sizeClasses = {
    sm: 'max-w-md', // 448px
    md: 'max-w-lg', // 512px
    lg: 'max-w-2xl', // 672px
    xl: 'max-w-4xl', // 896px
  };

  // Size in pixels for inline style fallback
  const sizePixels = {
    sm: 448, // max-w-md
    md: 512, // max-w-lg
    lg: 672, // max-w-2xl
    xl: 896, // max-w-4xl
  };

  // On mobile: full-screen modal with flex layout so footer stays visible
  // On desktop: centered modal with backdrop
  // Both are portalled to document.body so backdrop-filter on ancestors
  // cannot create a new containing block for position:fixed children.
  if (isMobile) {
    return createPortal(
      <div
        ref={panelRef}
        {...dialogProps}
        className={clsx(
          'fixed inset-0 z-50 flex h-dvh max-h-dvh flex-col overflow-hidden bg-white outline-none',
          mobileRootClassName
        )}
      >
        {/* Header */}
        {title && (
          <div
            className={clsx(
              'z-10 flex flex-shrink-0 items-center justify-between border-b bg-white p-3',
              headerClassName
            )}
          >
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="-mr-2 inline-flex min-h-10 min-w-10 items-center justify-center rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
                aria-label="חזור"
              >
                <ArrowRight size={20} />
              </button>
              <h2 id={titleId} className="text-lg font-semibold text-balance text-gray-900">
                {title}
              </h2>
            </div>
          </div>
        )}

        {/* Scrollable content - flex-1 min-h-0 allows overflow-y-auto to work */}
        <div className={clsx('min-h-0 flex-1 overflow-y-auto p-4', contentClassName)}>
          {children}
        </div>

        {/* Footer - always visible at bottom */}
        {footer && (
          <div
            className={clsx(
              'flex-shrink-0 border-t border-gray-100 bg-white px-4 py-3',
              footerClassName
            )}
          >
            {footer}
          </div>
        )}
      </div>,
      document.body
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className={clsx(
          'fixed inset-0 bg-[#0C1A55]/40 backdrop-blur-[10px] transition-opacity',
          backdropClassName
        )}
        onClick={closeOnBackdropClick ? onClose : undefined}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={panelRef}
          {...dialogProps}
          className={clsx(
            'relative w-full rounded-[18px] bg-white/98 shadow-2xl transition-shadow duration-200 outline-none',
            'border border-white/90 backdrop-blur-sm',
            sizeClasses[size],
            panelClassName
          )}
          style={{ maxWidth: sizePixels[size] }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          {title && (
            <div
              className={clsx(
                'flex items-center justify-between border-b border-[rgba(60,95,168,0.1)] p-4',
                headerClassName
              )}
            >
              <h2 id={titleId} className="text-xl font-semibold text-balance text-gray-900">
                {title}
              </h2>
              <button
                onClick={onClose}
                className="inline-flex min-h-10 min-w-10 items-center justify-center rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                type="button"
                aria-label="סגור"
              >
                <X size={24} />
              </button>
            </div>
          )}

          {/* Content */}
          <div className={clsx('p-6', contentClassName)}>{children}</div>

          {/* Footer */}
          {footer && (
            <div
              className={clsx(
                'border-t border-[rgba(60,95,168,0.1)] bg-[rgba(248,250,252,0.8)] px-6 py-4',
                footerClassName
              )}
            >
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
